import * as React from "react"
import { toast } from "react-toastify"
import { Box, Typography } from "@mui/material"
import { ButtonSeeker } from "@ui-seeker/core"

// Same hook as @ui-seeker/app 0.44.11: same defaults, same promise callbacks, same confirm toast

const DEFAULT_ERROR = "Server Error: Please try again later."
const DEFAULT_SUCCESS = "Data loaded"

const buttonStyles = {
    py: 0,
    px: 0.5,
    fontSize: "0.9rem",
}

function AppConfirmToast({ msg, onSubmit, submitText = "Ok", onCancel, cancelText = "Cancel" }) {
    return <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body1">{msg}</Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <ButtonSeeker variant="contained" sx={buttonStyles} onClick={onSubmit}>{submitText}</ButtonSeeker>
            <ButtonSeeker sx={buttonStyles} onClick={onCancel}>{cancelText}</ButtonSeeker>
        </Box>
    </Box>
}

export default function useToastSeeker() {
    const basePromise = (callback, { pending, success, error, ...config } = {}) => toast.promise(
        callback.then((res) => res).finally(() => config.onFinish && config.onFinish()),
        {
            pending: {
                render: () => {
                    if (config.onPending) return config.onPending() || pending || "Loading..."
                    return pending || "Loading..."
                },
            },
            success: {
                render: (res) => {
                    if (config.onSuccess) return config.onSuccess(res.data) || success || DEFAULT_SUCCESS
                    return success || DEFAULT_SUCCESS
                },
            },
            error: {
                render: (err) => {
                    if (config.onError) return config.onError(err.data) || error || DEFAULT_ERROR
                    return error || DEFAULT_ERROR
                },
            },
        },
        config
    )

    const baseConfirm = (msg, { onSubmit, submitText = "Ok", onCancel, cancelText = "Cancel", ...config } = {}) => toast.info(
        <AppConfirmToast msg={msg} onSubmit={onSubmit} submitText={submitText} onCancel={onCancel} cancelText={cancelText}/>,
        { closeButton: false, autoClose: 8e3, ...config }
    )

    return {
        showToast: {
            default: (msg, config = {}) => toast(msg, config),
            error: (msg = DEFAULT_ERROR, config = {}) => toast.error(msg, config),
            warning: (msg = "No data available.", config = {}) => toast.warn(msg, config),
            success: (msg = DEFAULT_SUCCESS, config = {}) => toast.success(msg, config),
            info: (msg = "", config = {}) => toast.info(msg, config),
            promise: basePromise,
            confirm: baseConfirm,
        },
    }
}
